/**
 * Label Schema Compiler
 *
 * Converts a visual LabelSchema definition into a ClassificationPattern
 * that can be used by the classification engine.
 *
 * Segments are defined by character ranges in the sample label text.
 * Characters NOT covered by any segment become literal regex matchers.
 *
 * Example: Sample "1.INV11.STR16(5.PV23)" with segments:
 *   [0,1)→station, [2,7)→inverter, [8,13)→string, [14,15)→skip, [16,20)→pv
 *   Gaps: "." "." "(" "." ")"
 *   regex: ^(\d+)\.(INV\d+)\.(STR\d+)\(\d+\.(PV\d+)\)$
 *   captureGroups: { station: 1, inverter: 2, string: 3, pv: 4 }
 */

import type {
  LabelSchema,
  LabelSchemaSegment,
  ClassificationPattern,
  LayoutTextEntity,
  TopologyFields,
} from './types';

/**
 * Detect prefix and numeric parts in a segment value.
 * "DCCB03" → { prefix: "DCCB", number: "03" }
 * "2"      → { prefix: "",     number: "2" }
 * "STR06"  → { prefix: "STR",  number: "06" }
 * "ABC"    → { prefix: "ABC",  number: "" }
 */
export function detectSegmentParts(segment: string): {
  prefix: string;
  number: string;
} {
  const match = segment.match(/^([A-Za-z]*)(\d*)$/);
  if (!match) return { prefix: segment, number: '' };
  return { prefix: match[1], number: match[2] };
}

/**
 * Build a regex fragment for a single captured segment.
 *
 * - Pure number "2" → (\d+)
 * - Prefix+number "DCCB03" → (DCCB\d+)
 * - Pure text "ABC" → (ABC\w*)  (flexible match)
 * - flexible=true → ([A-Za-z0-9]+) (any alphanumeric)
 */
function segmentToRegex(segmentValue: string, flexible?: boolean): string {
  if (flexible) return `([A-Za-z0-9]+)`;

  const { prefix, number } = detectSegmentParts(segmentValue);

  if (prefix && number) {
    return `(${escapeRegex(prefix)}\\d+)`;
  }
  if (!prefix && number) {
    return `(\\d+)`;
  }
  return `(${escapeRegex(prefix)}\\w*)`;
}

/**
 * Build a regex fragment for a skipped segment (non-capturing).
 */
function skipToRegex(segmentValue: string, flexible?: boolean): string {
  if (flexible) return `[A-Za-z0-9]+`;

  const { prefix, number } = detectSegmentParts(segmentValue);
  if (prefix && number) return `${escapeRegex(prefix)}\\d+`;
  if (number) return `\\d+`;
  return escapeRegex(segmentValue);
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Compile a LabelSchema (character-range segments) into a ClassificationPattern.
 */
export function compileSchema(schema: LabelSchema): ClassificationPattern {
  const { sampleLabel, segments } = schema;
  const sorted = [...segments].sort((a, b) => a.start - b.start);

  const regexParts: string[] = [];
  const captureGroups: Record<string, number> = {};
  let groupIndex = 0;
  let pos = 0;

  for (const seg of sorted) {
    // Add literal text for any gap before this segment
    if (seg.start > pos) {
      const gap = sampleLabel.slice(pos, seg.start);
      regexParts.push(escapeRegex(gap));
    }

    const text = sampleLabel.slice(seg.start, seg.end);

    if (seg.field === 'skip') {
      regexParts.push(skipToRegex(text, seg.flexible));
    } else {
      groupIndex++;
      regexParts.push(segmentToRegex(text, seg.flexible));
      captureGroups[seg.field] = groupIndex;
    }

    pos = seg.end;
  }

  // Add literal text after last segment
  if (pos < sampleLabel.length) {
    regexParts.push(escapeRegex(sampleLabel.slice(pos)));
  }

  const regexStr = `^${regexParts.join('')}$`;

  return {
    id: 'schema-auto-1',
    regex: regexStr,
    level: schema.level,
    captureGroups,
    priority: 1,
    builtin: false,
  };
}

/**
 * Test a compiled pattern against text entities.
 * Returns match count and up to 5 sample extractions.
 */
export function testSchemaPattern(
  pattern: ClassificationPattern,
  entities: LayoutTextEntity[],
): {
  matchCount: number;
  totalEntities: number;
  samples: Array<{
    text: string;
    fields: Partial<TopologyFields>;
  }>;
} {
  let re: RegExp;
  try {
    re = new RegExp(pattern.regex, 'i');
  } catch {
    return { matchCount: 0, totalEntities: entities.length, samples: [] };
  }

  let matchCount = 0;
  const samples: Array<{ text: string; fields: Partial<TopologyFields> }> = [];

  for (const entity of entities) {
    const m = entity.normalizedText.match(re);
    if (m) {
      matchCount++;
      if (samples.length < 5) {
        const fields: Partial<TopologyFields> = {};
        for (const [fieldName, groupIdx] of Object.entries(pattern.captureGroups)) {
          if (m[groupIdx]) {
            (fields as any)[fieldName] = m[groupIdx];
          }
        }
        samples.push({ text: entity.normalizedText, fields });
      }
    }
  }

  return { matchCount, totalEntities: entities.length, samples };
}

/**
 * Auto-detect prefix from a segment value for display.
 * "DCCB03" → "DCCB"
 * "STR06"  → "STR"
 * "2"      → ""
 */
export function detectPrefix(segmentValue: string): string {
  return detectSegmentParts(segmentValue).prefix;
}

/**
 * Auto-detect segments from a sample label.
 * Finds runs of alphanumeric characters and treats punctuation as separators.
 * Returns segment ranges with auto-detected field assignments.
 */
export function autoDetectSegments(sampleLabel: string): LabelSchemaSegment[] {
  const segments: LabelSchemaSegment[] = [];
  const re = /[A-Za-z0-9]+/g;
  let match: RegExpExecArray | null;
  let fieldIndex = 0;

  while ((match = re.exec(sampleLabel)) !== null) {
    const text = match[0];
    const start = match.index;
    const end = start + text.length;
    const prefix = detectPrefix(text).toUpperCase();
    const partUpper = text.toUpperCase();

    let field = 'skip';

    // Auto-detect field from prefix
    if (prefix === 'DCCB' || prefix === 'CB' || prefix === 'DC') field = 'dccb';
    else if (prefix === 'STR' || prefix === 'STRING') field = 'string';
    else if (prefix === 'INV' || prefix === 'INVERTER') field = 'inverter';
    else if (prefix === 'MPPT') field = 'mppt';
    else if (prefix === 'MOD' || prefix === 'MODULE') field = 'module';
    else if (prefix === 'STA' || prefix === 'STATION' || prefix === 'TR') field = 'station';
    else if (partUpper.includes('MPPT')) field = 'mppt';
    else if (/^\d+$/.test(text)) {
      // Pure number — guess based on position
      if (fieldIndex === 0) field = 'station';
      else if (fieldIndex === 1) field = 'mppt';
    }

    segments.push({ field, start, end, prefix: prefix || undefined });
    fieldIndex++;
  }

  return segments;
}

/**
 * Compile multiple schemas into an array of ClassificationPatterns.
 * Each schema gets a unique id and priority.
 */
export function compileSchemas(schemas: LabelSchema[]): ClassificationPattern[] {
  return schemas.map((schema, i) => {
    const pattern = compileSchema(schema);
    return { ...pattern, id: `schema-auto-${i}`, priority: i + 1 };
  });
}

/**
 * Suggest the classification level based on the deepest topology field in segments.
 * e.g., segments with 'station' + 'dccb' + 'string' → suggests 'string' (deepest).
 */
const LEVEL_DEPTH: Record<string, number> = {
  station: 1, inverter: 2, mppt: 3, dccb: 4, string: 5, module: 6,
};

export function suggestLevelFromSegments(segments: LabelSchemaSegment[]): string {
  let deepest = 'string';
  let deepestDepth = 0;
  for (const seg of segments) {
    const d = LEVEL_DEPTH[seg.field] ?? 0;
    if (d > deepestDepth) {
      deepestDepth = d;
      deepest = seg.field;
    }
  }
  return deepest;
}
